<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    required: true,
    validator: value => ['won', 'lost'].includes(value),
  },
  dealValue: {
    type: Number,
    default: 0,
  },
  dealCurrency: {
    type: String,
    default: 'BRL',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel']);

const { t } = useI18n();

const reason = ref('');

const isWon = computed(() => props.type === 'won');

const dialogTitle = computed(() => {
  return isWon.value
    ? t('CRM.DEAL.MARK_WON_TITLE')
    : t('CRM.DEAL.MARK_LOST_TITLE');
});

const dialogDescription = computed(() => {
  return isWon.value
    ? t('CRM.DEAL.MARK_WON_DESC')
    : t('CRM.DEAL.MARK_LOST_DESC');
});

const confirmButtonText = computed(() => {
  return isWon.value ? t('CRM.DEAL.CONFIRM_WON') : t('CRM.DEAL.CONFIRM_LOST');
});

const reasonPlaceholder = computed(() => {
  return isWon.value
    ? t('CRM.DEAL.WON_REASON_PLACEHOLDER')
    : t('CRM.DEAL.LOST_REASON_PLACEHOLDER');
});

const currencySymbols = {
  USD: '$',
  EUR: '€',
  BRL: 'R$',
};

const formattedValue = computed(() => {
  if (!props.dealValue) return '';
  const symbol = currencySymbols[props.dealCurrency] || 'R$';
  return `${symbol} ${props.dealValue.toFixed(2)}`;
});

function handleConfirm() {
  emit('confirm', reason.value);
  reason.value = '';
}

function handleCancel() {
  emit('cancel');
  reason.value = '';
}
</script>

<template>
  <woot-modal :show="isOpen" :on-close="handleCancel">
    <div class="deal-dialog">
      <div class="deal-dialog__header">
        <div
          class="deal-dialog__icon"
          :class="isWon ? 'deal-dialog__icon--won' : 'deal-dialog__icon--lost'"
        >
          <fluent-icon
            :icon="isWon ? 'checkmark-circle' : 'dismiss-circle'"
            size="24"
          />
        </div>
        <h3 class="deal-dialog__title">
          {{ dialogTitle }}
        </h3>
      </div>

      <p class="deal-dialog__description">
        {{ dialogDescription }}
      </p>

      <div v-if="formattedValue" class="deal-dialog__value">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <span class="deal-dialog__value-label">
          {{ t('CRM.DEAL.VALUE') }}:
        </span>
        <span class="deal-dialog__value-amount">
          {{ formattedValue }}
        </span>
      </div>

      <div class="deal-dialog__reason">
        <label class="deal-dialog__reason-label">
          {{ isWon ? t('CRM.DEAL.WON_REASON') : t('CRM.DEAL.LOST_REASON') }}
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <span class="deal-dialog__optional">
            ({{ t('CRM.DEAL.OPTIONAL') }})
          </span>
        </label>
        <textarea
          v-model="reason"
          class="deal-dialog__reason-input"
          :placeholder="reasonPlaceholder"
          rows="3"
        />
      </div>

      <div class="deal-dialog__actions">
        <button
          class="deal-dialog__btn deal-dialog__btn--cancel"
          :disabled="isLoading"
          @click="handleCancel"
        >
          {{ t('CRM.DEAL.CANCEL') }}
        </button>
        <button
          class="deal-dialog__btn"
          :class="isWon ? 'deal-dialog__btn--won' : 'deal-dialog__btn--lost'"
          :disabled="isLoading"
          @click="handleConfirm"
        >
          <span v-if="isLoading">{{ t('CRM.DEAL.PROCESSING') }}</span>
          <span v-else>{{ confirmButtonText }}</span>
        </button>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.deal-dialog {
  @apply p-6;
}

.deal-dialog__header {
  @apply flex items-center gap-3 mb-4;
}

.deal-dialog__icon {
  @apply w-12 h-12 rounded-full flex items-center justify-center;
}

.deal-dialog__icon--won {
  @apply bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400;
}

.deal-dialog__icon--lost {
  @apply bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400;
}

.deal-dialog__title {
  @apply text-lg font-semibold text-slate-800 dark:text-slate-100;
}

.deal-dialog__description {
  @apply text-sm text-slate-600 dark:text-slate-400 mb-4;
}

.deal-dialog__value {
  @apply flex items-center gap-2 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg mb-4;
}

.deal-dialog__value-label {
  @apply text-sm text-slate-600 dark:text-slate-400;
}

.deal-dialog__value-amount {
  @apply text-lg font-semibold text-slate-800 dark:text-slate-200;
}

.deal-dialog__reason {
  @apply mb-4;
}

.deal-dialog__reason-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.deal-dialog__optional {
  @apply text-slate-500 dark:text-slate-500 font-normal;
}

.deal-dialog__reason-input {
  @apply w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 resize-none;
}

.deal-dialog__actions {
  @apply flex justify-end gap-3;
}

.deal-dialog__btn {
  @apply px-4 py-2 text-sm font-medium rounded-md transition-colors;
}

.deal-dialog__btn--cancel {
  @apply text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700;
}

.deal-dialog__btn--won {
  @apply bg-green-500 text-white hover:bg-green-600 disabled:opacity-50;
}

.deal-dialog__btn--lost {
  @apply bg-red-500 text-white hover:bg-red-600 disabled:opacity-50;
}
</style>
